//FleetX-Security-CRUD/web-admin/src/pages/Vehicles.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import { API_ORIGIN } from "../api/apiConfig";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import {
  getVehicleDetailLine,
  getVehicleIdentifierText,
  getVehicleName,
} from "../utils/vehicleDisplay";
import "../styles/admin.css";

const categoryOptions = [
  "Sedan",
  "SUV",
  "Van",
  "Hatchback",
  "MPV",
  "Pickup",
  "Crossover",
  "Byd",
  "Montero",
];

const transmissionOptions = ["Automatic", "Manual"];
const fuelOptions = ["Gasoline", "Diesel", "Electric", "Hybrid"];
const codingOptions = [
  "None",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

const MAX_VEHICLE_IMAGES = 3;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const DESCRIPTION_MAX_LENGTH = 300;
const FEATURES_MAX_LENGTH = 200;
const ITEMS_PER_PAGE = 10;
const actionIcons = {
  edit: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 17.2V20h2.8L17.1 9.7l-2.8-2.8L4 17.2Zm15.7-10.1c.4-.4.4-1 0-1.4l-1.4-1.4a1 1 0 0 0-1.4 0l-1.1 1.1 2.8 2.8 1.1-1.1Z" />
    </svg>
  ),
  pause: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 5h4v14H7V5Zm6 0h4v14h-4V5Z" />
    </svg>
  ),
  play: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5v14l11-7L8 5Z" />
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 4h8l1 2h4v2H3V6h4l1-2Zm1 6h2v8H9v-8Zm4 0h2v8h-2v-8Zm4 0h2v10H5V10h2v8h10v-8Z" />
    </svg>
  ),
};

const getVehicleImages = (vehicle) => {
  const images = Array.isArray(vehicle?.images) ? vehicle.images.filter(Boolean) : [];
  if (images.length) return images.slice(0, MAX_VEHICLE_IMAGES);
  return vehicle?.image ? [vehicle.image] : [];
};

const getImageSrc = (src) => {
  if (!src) return "";
  if (src.startsWith("blob:") || src.startsWith("http://") || src.startsWith("https://")) {
    return src;
  }
  if (src.startsWith("/uploads/")) return `${API_ORIGIN}${src}`;
  return src;
};

const initialForm = {
  plateNo: "",
  make: "",
  model: "",
  year: "",
  color: "",
  dailyRate: "",
  rate24Hr: "",
  extraHourRate: "",
  location: "",
  locationPlaceId: "",
  locationLatitude: null,
  locationLongitude: null,
  locationRequiresSelection: true,
  status: "available",
  category: "Sedan",
  seater: "5",
  transmission: "Automatic",
  fuel: "Gasoline",
  coding: "None",
  description: "",
  luggageBags: "",
  doors: "",
  aircon: "",
  engine: "",
  fuelEconomy: "",
  features: "",
};

function normalizeVehicleStatus(value) {
  const status = String(value || "").trim().toLowerCase();
  if (status === "booked" || status === "rented") return "available";
  return status;
}

function hasOngoingBooking(vehicle) {
  return Boolean(
    vehicle?.ongoingToday ||
      vehicle?.hasOngoingBooking ||
      vehicle?.currentBooking ||
      vehicle?.currentBookingRef
  );
}

function getFleetStatus(vehicle) {
  return normalizeVehicleStatus(vehicle?.status) === "maintenance"
    ? "maintenance"
    : "available";
}

function getAvailabilityTodayStatus(vehicle) {
  if (hasOngoingBooking(vehicle)) return "ongoing";
  if (vehicle?.isActive === false || getFleetStatus(vehicle) === "maintenance") {
    return "not_available";
  }
  return "available";
}

function getOperationalStatus(vehicle) {
  if (vehicle?.isArchived) return "archived";
  if (vehicle?.isActive === false) return "inactive";
  if (getFleetStatus(vehicle) === "maintenance") return "maintenance";
  if (hasOngoingBooking(vehicle)) return "ongoing";
  return "available";
}

function vehicleMatchesStatusFilter(vehicle, filter) {
  if (filter === "all") return !vehicle?.isArchived;
  if (vehicle?.isArchived) return false;
  if (filter === "maintenance") return getFleetStatus(vehicle) === "maintenance";
  if (filter === "ongoing") return hasOngoingBooking(vehicle);
  if (filter === "available") return getOperationalStatus(vehicle) === "available";
  return getOperationalStatus(vehicle) === filter;
}

function getVehicleStatFilterKey(filter) {
  if (filter === "available") return "available";
  if (filter === "ongoing") return "ongoing";
  if (filter === "maintenance") return "maintenance";
  return "all";
}

export default function Vehicles() {
  const { user } = useAuth();
  const location = useLocation();

  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [msg, setMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialForm);
  const [imageItems, setImageItems] = useState([]);
  const [modalNotice, setModalNotice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [archiveModal, setArchiveModal] = useState(null);
  const uploadInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceIndexRef = useRef(null);
  const imageItemsRef = useRef([]);

  const isAdmin = user?.role === "technical_admin" || user?.role === "admin";
  const isStaff = user?.role === "staff";
  const canAccessPage = isAdmin || isStaff;
  const canAddVehicle = isAdmin;
  const canEditVehicle = isAdmin || isStaff;
  const canManageVehicleStatus = isAdmin;
  const canArchiveVehicle = isAdmin;

  const query = useMemo(
    () => ({
      search,
      status: activeFilter,
    }),
    [search, activeFilter]
  );

  const loadVehicles = async ({ silent = false } = {}) => {
    if (!silent) setMsg("");
    try {
      const response = await api.get("/admin/vehicles", { params: query });
      setVehicles(response.data?.vehicles || []);
    } catch (err) {
      console.error("Load vehicles error:", err?.response?.data?.message || err.message);
      if (!silent) {
        setMsg(err?.response?.data?.message || "Failed to load vehicles.");
      }
    }
  };

  useEffect(() => {
    if (canAccessPage) {
      loadVehicles();
    }
    // eslint-disable-next-line
  }, [query.search, query.status, canAccessPage]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedVehicleIds([]);
  }, [search, activeFilter, vehicleStatusFilter, categoryFilter]);

  useEffect(() => {
    const incomingFilter = location.state?.vehicleStatusFilter;
    const validFilters = ["all", "available", "ongoing", "maintenance"];
    if (incomingFilter && validFilters.includes(incomingFilter)) {
      setVehicleStatusFilter(incomingFilter);
    }
  }, [location.state?.vehicleStatusFilter]);

  useEffect(() => {
    imageItemsRef.current = imageItems;
  }, [imageItems]);

  useEffect(() => {
    return () => {
      imageItemsRef.current.forEach((item) => {
        if (item.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const resetForm = () => {
    imageItemsRef.current.forEach((item) => {
      if (item.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    setFormData(initialForm);
    setImageItems([]);
    setModalNotice("");
    setErrors({});
    setEditingId(null);
    setIsSubmitting(false);
    setIsDraggingImages(false);
  };

  const setImageError = (message) => {
    setErrors((current) => ({ ...current, images: message }));
  };

  const clearImageError = () => {
    setErrors((current) => {
      const next = { ...current };
      delete next.images;
      return next;
    });
  };

  const createImageItem = (file) => ({
    id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file),
    existingUrl: "",
  });

  const validateImageFiles = (files) => {
    const validFiles = [];

    files.forEach((file) => {
      if (!file.type?.startsWith("image/")) {
        setImageError("Only image files are allowed.");
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        setImageError("Each image must be 5MB or smaller.");
        return;
      }

      validFiles.push(file);
    });

    return validFiles;
  };

  const addImageFiles = (fileList) => {
    const files = validateImageFiles(Array.from(fileList || []));
    if (!files.length) return;

    setImageItems((current) => {
      const availableSlots = MAX_VEHICLE_IMAGES - current.length;
      if (availableSlots <= 0) {
        setImageError("You can upload up to 3 images.");
        return current;
      }

      clearImageError();
      return [...current, ...files.slice(0, availableSlots).map(createImageItem)];
    });
  };

  const replaceImageFile = (fileList) => {
    const [file] = validateImageFiles(Array.from(fileList || []));
    const targetIndex = replaceIndexRef.current;

    if (!file || targetIndex === null) return;

    setImageItems((current) => {
      if (!current[targetIndex]) return current;

      const next = [...current];
      if (next[targetIndex].previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(next[targetIndex].previewUrl);
      }
      next[targetIndex] = createImageItem(file);
      clearImageError();
      return next;
    });

    replaceIndexRef.current = null;
  };

  const removeImage = (index) => {
    setImageItems((current) => {
      const next = current.filter((item, itemIndex) => {
        if (itemIndex === index && item.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
        return itemIndex !== index;
      });
      clearImageError();
      return next;
    });
  };

  const moveImage = (index, direction) => {
    setImageItems((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;

      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const openReplacePicker = (index) => {
    replaceIndexRef.current = index;
    replaceInputRef.current?.click();
  };

  const handleImageDrop = (event) => {
    event.preventDefault();
    setIsDraggingImages(false);
    addImageFiles(event.dataTransfer.files);
  };

  const openAddModal = () => {
    if (!canAddVehicle) return;
    resetForm();
    setMsg("");
    setShowModal(true);
  };

  const openEditModal = (vehicle) => {
    if (!canEditVehicle) return;

    setErrors({});
    setModalNotice("");
    setMsg("");
    setEditingId(vehicle._id);
    setFormData({
      plateNo: vehicle.plateNo || "",
      make: vehicle.make || "",
      model: vehicle.model || "",
      year: String(vehicle.year || ""),
      color: vehicle.color || "",
      dailyRate: String(vehicle.dailyRate || ""),
      rate24Hr: String(vehicle.rate24Hr || vehicle.dailyRate || ""),
      extraHourRate: String(vehicle.extraHourRate || ""),
      location: vehicle.location || "",
      locationPlaceId: vehicle.locationPlaceId || "",
      locationLatitude: vehicle.locationCoords?.lat ?? null,
      locationLongitude: vehicle.locationCoords?.lng ?? null,
      locationRequiresSelection: false,
      status: normalizeVehicleStatus(vehicle.status) === "maintenance" ? "maintenance" : "available",
      category: vehicle.category || "Sedan",
      seater: String(vehicle.seater || 5),
      transmission: vehicle.transmission || "Automatic",
      fuel: vehicle.fuel || "Gasoline",
      coding: vehicle.coding || "None",
      description: vehicle.description || "",
      luggageBags:
        vehicle.luggageBags || vehicle.luggageBags === 0
          ? String(vehicle.luggageBags)
          : "",
      doors: vehicle.doors || vehicle.doors === 0 ? String(vehicle.doors) : "",
      aircon: vehicle.aircon || "",
      engine: vehicle.engine || "",
      fuelEconomy: vehicle.fuelEconomy || "",
      features: Array.isArray(vehicle.features)
        ? vehicle.features.join("\n")
        : vehicle.features || "",
    });
    setImageItems(
      getVehicleImages(vehicle).map((url, index) => ({
        id: `${vehicle._id}-image-${index}`,
        file: null,
        previewUrl: getImageSrc(url),
        existingUrl: url,
      }))
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validateVehicleForm = () => {
    const nextErrors = {};
    const currentYear = new Date().getFullYear() + 1;
    const yearValue = Number(formData.year);
    const rate24HrValue = Number(formData.rate24Hr || formData.dailyRate);
    const extraHourRateValue = Number(formData.extraHourRate);
    const seaterValue = Number(formData.seater);
    const luggageValue = Number(formData.luggageBags);
    const doorsValue = Number(formData.doors);

    if (!formData.plateNo.trim()) {
      nextErrors.plateNo = "Plate Number is required.";
    } else if (!/^[A-Z0-9-\s]{3,15}$/.test(formData.plateNo.trim().toUpperCase())) {
      nextErrors.plateNo =
        "Plate Number must be 3 to 15 characters using letters, numbers, spaces, or dashes.";
    }

    if (!formData.make.trim()) {
      nextErrors.make = "Make is required.";
    } else if (formData.make.trim().length < 2) {
      nextErrors.make = "Make must be at least 2 characters.";
    }

    if (!formData.model.trim()) {
      nextErrors.model = "Model is required.";
    }

    if (!formData.year.trim()) {
      nextErrors.year = "Year is required.";
    } else if (!/^\d{4}$/.test(formData.year.trim())) {
      nextErrors.year = "Year must be exactly 4 digits.";
    } else if (yearValue < 1990 || yearValue > currentYear) {
      nextErrors.year = `Year must be between 1990 and ${currentYear}.`;
    }

    if (!formData.color.trim()) {
      nextErrors.color = "Color is required.";
    } else if (formData.color.trim().length > 40) {
      nextErrors.color = "Color must be 40 characters or less.";
    }

    if (!formData.rate24Hr.trim()) {
      nextErrors.rate24Hr = "Daily rate is required.";
    } else if (Number.isNaN(rate24HrValue) || rate24HrValue <= 0) {
      nextErrors.rate24Hr = "Daily rate must be greater than 0.";
    }

    if (!formData.extraHourRate.trim()) {
      nextErrors.extraHourRate = "Extra-hour rate is required.";
    } else if (Number.isNaN(extraHourRateValue) || extraHourRateValue <= 0) {
      nextErrors.extraHourRate = "Extra-hour rate must be greater than 0.";
    }

    if (!formData.location.trim()) {
      nextErrors.location = "Location is required.";
    }

    if (formData.location.trim() && formData.locationRequiresSelection) {
      nextErrors.location = String.fromCharCode(83, 101, 108, 101, 99, 116, 32, 97, 32, 118, 97, 108, 105, 100, 32, 71, 111, 111, 103, 108, 101, 32, 97, 100, 100, 114, 101, 115, 115, 32, 102, 114, 111, 109, 32, 116, 104, 101, 32, 115, 117, 103, 103, 101, 115, 116, 105, 111, 110, 115, 46);
    }

    if (!formData.category.trim()) {
      nextErrors.category = "Category is required.";
    }

    if (!formData.seater.trim()) {
      nextErrors.seater = "Seater is required.";
    } else if (!Number.isInteger(seaterValue) || seaterValue < 1 || seaterValue > 100) {
      nextErrors.seater = "Seater must be a whole number between 1 and 100.";
    }

    if (!transmissionOptions.includes(formData.transmission)) {
      nextErrors.transmission = "Please select a valid transmission.";
    }

    if (!fuelOptions.includes(formData.fuel)) {
      nextErrors.fuel = "Please select a valid fuel type.";
    }

    if (!codingOptions.includes(formData.coding)) {
      nextErrors.coding = "Please select a valid coding day.";
    }

    if (formData.description.length > DESCRIPTION_MAX_LENGTH) {
      nextErrors.description = `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less.`;
    }

    if (
      formData.luggageBags.trim() &&
      (!Number.isInteger(luggageValue) || luggageValue < 0 || luggageValue > 30)
    ) {
      nextErrors.luggageBags = "Luggage must be a whole number between 0 and 30.";
    }

    if (
      formData.doors.trim() &&
      (!Number.isInteger(doorsValue) || doorsValue < 0 || doorsValue > 20)
    ) {
      nextErrors.doors = "Doors must be a whole number between 0 and 20.";
    }

    if (formData.aircon.length > 50) {
      nextErrors.aircon = "Air conditioning detail must be 50 characters or less.";
    }

    if (formData.engine.length > 80) {
      nextErrors.engine = "Engine detail must be 80 characters or less.";
    }

    if (formData.fuelEconomy.length > 50) {
      nextErrors.fuelEconomy = "Fuel economy must be 50 characters or less.";
    }

    if (formData.features.length > FEATURES_MAX_LENGTH) {
      nextErrors.features = `Features must be ${FEATURES_MAX_LENGTH} characters or less.`;
    }

    if (imageItems.length === 0) {
      nextErrors.images = "Add at least 1 vehicle photo before publishing.";
    }

    if (imageItems.length > MAX_VEHICLE_IMAGES) {
      nextErrors.images = "You can upload up to 3 images.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitVehicle = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!canEditVehicle) {
      setMsg("You are not allowed to perform this action.");
      return;
    }

    if (!validateVehicleForm()) {
      setMsg("Please fix the highlighted fields.");
      return;
    }

    const payload = new FormData();
    payload.append("plateNo", formData.plateNo.trim().toUpperCase());
    payload.append("make", formData.make.trim());
    payload.append("model", formData.model.trim());
    payload.append("year", Number(formData.year));
    payload.append("color", formData.color.trim());
    payload.append("dailyRate", Number(formData.rate24Hr || formData.dailyRate));
    payload.append("rate24Hr", Number(formData.rate24Hr || formData.dailyRate));
    payload.append("extraHourRate", Number(formData.extraHourRate));
    payload.append("location", formData.location.trim());
    payload.append("locationPlaceId", formData.locationPlaceId);
    if (formData.locationLatitude !== null && formData.locationLongitude !== null) {
      payload.append("locationLatitude", formData.locationLatitude);
      payload.append("locationLongitude", formData.locationLongitude);
    }
    payload.append("status", formData.status);
    payload.append("category", formData.category.trim());
    payload.append("seater", Number(formData.seater));
    payload.append("transmission", formData.transmission);
    payload.append("fuel", formData.fuel);
    payload.append("coding", formData.coding);
    payload.append("description", formData.description.trim());
    payload.append("luggageBags", formData.luggageBags.trim());
    payload.append("doors", formData.doors.trim());
    payload.append("aircon", formData.aircon.trim());
    payload.append("engine", formData.engine.trim());
    payload.append("fuelEconomy", formData.fuelEconomy.trim());
    payload.append(
      "features",
      JSON.stringify(
        formData.features
          .split(/\r?\n|,/)
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
    payload.append(
      "existingImages",
      JSON.stringify(imageItems.filter((item) => item.existingUrl).map((item) => item.existingUrl))
    );

    const imageOrder = [];
    let newImageIndex = 0;
    imageItems.forEach((item) => {
      if (item.file) {
        payload.append("images", item.file);
        imageOrder.push({ type: "new", index: newImageIndex });
        newImageIndex += 1;
      } else if (item.existingUrl) {
        imageOrder.push({ type: "existing", url: item.existingUrl });
      }
    });
    payload.append("imageOrder", JSON.stringify(imageOrder));

    try {
      setIsSubmitting(true);
      if (editingId) {
        await api.put(`/admin/vehicles/${editingId}`, payload);
        setMsg("Vehicle updated successfully.");
      } else {
        await api.post("/admin/vehicles", payload);
        setMsg("Vehicle created successfully.");
      }

      closeModal();
      await loadVehicles({ silent: true });
    } catch (err) {
      console.error("Submit vehicle error:", err?.response?.data?.message || err.message);

      if (err?.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }

      setMsg(err?.response?.data?.message || "Failed to save vehicle.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDraftNotice = () => {
    setModalNotice("Draft mode is kept local in this modal. Publish the vehicle to save it to FleetX.");
  };

  const runVehicleAction = async ({ id, action, loadingLabel, request, successMessage, failureMessage }) => {
    if (!id || actionLoadingId) return;
    setMsg("");
    setConfirmAction(null);
    setActionLoadingId(`${action}:${id}`);
    try {
      await request();
      await loadVehicles({ silent: true });
      if (successMessage) setMsg(successMessage);
    } catch (err) {
      setMsg(err?.response?.data?.message || failureMessage);
    } finally {
      setActionLoadingId("");
    }
  };

  const deactivate = async (id) => {
    if (!canManageVehicleStatus) return;
    setConfirmAction({
      id,
      title: "Deactivate vehicle?",
      message: "This vehicle will be hidden from customer booking availability until it is activated again.",
      confirmLabel: "Deactivate Vehicle",
      loadingLabel: "Deactivating...",
      tone: "warning",
      onConfirm: () =>
        runVehicleAction({
          id,
          action: "deactivate",
          request: () => api.patch(`/admin/vehicles/${id}/deactivate`),
          successMessage: "Vehicle deactivated successfully.",
          failureMessage: "Failed to deactivate vehicle.",
        }),
    });
  };

  const activate = async (id) => {
    if (!canManageVehicleStatus) return;
    setConfirmAction({
      id,
      title: "Activate vehicle?",
      message: "This vehicle will become available for customer booking searches when its status allows it.",
      confirmLabel: "Activate Vehicle",
      loadingLabel: "Activating...",
      onConfirm: () =>
        runVehicleAction({
          id,
          action: "activate",
          request: () => api.patch(`/admin/vehicles/${id}/activate`),
          successMessage: "Vehicle activated successfully.",
          failureMessage: "Failed to activate vehicle.",
        }),
    });
  };

  const openArchiveModal = (vehicle) => {
    if (!canArchiveVehicle) return;
    setArchiveModal({ vehicle, reason: "", error: "", submitting: false });
  };

  const confirmArchive = async () => {
    if (!archiveModal?.vehicle?._id || archiveModal.submitting) return;
    const reason = String(archiveModal.reason || "").trim();
    if (!reason) {
      setArchiveModal((prev) => ({ ...prev, error: "Please enter a reason for archiving." }));
      return;
    }
    setArchiveModal((prev) => ({ ...prev, error: "", submitting: true }));
    try {
      await api.patch(`/admin/vehicles/${archiveModal.vehicle._id}/archive`, { reason });
      setArchiveModal(null);
      await loadVehicles({ silent: true });
      setMsg("Vehicle archived successfully.");
    } catch (err) {
      setArchiveModal((prev) => ({
        ...prev,
        submitting: false,
        error: err?.response?.data?.message || "Failed to archive vehicle.",
      }));
    }
  };

  const restore = async (id) => {
    if (!canArchiveVehicle) return;
    setConfirmAction({
      id,
      title: "Restore vehicle?",
      message: "This vehicle will be restored from the archive and set back to active.",
      confirmLabel: "Restore Vehicle",
      loadingLabel: "Restoring...",
      onConfirm: () =>
        runVehicleAction({
          id,
          action: "restore",
          request: () => api.patch(`/admin/vehicles/${id}/restore`),
          successMessage: "Vehicle restored successfully.",
          failureMessage: "Failed to restore vehicle.",
        }),
    });
  };

  const vehiclesWithBookingAvailability = vehicles;

  const filteredVehicles = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const isArchivedTab = activeFilter === "archived";
    return vehiclesWithBookingAvailability.filter((v) => {
      const matchesVehicleStatus = isArchivedTab
        ? Boolean(v.isArchived)
        : vehicleMatchesStatusFilter(v, vehicleStatusFilter);
      const matchesCategory =
        categoryFilter === "all" || v.category === categoryFilter;
      const searchableText = [
        v.plateNo,
        v.plateNumber,
        v.licensePlate,
        v.unitCode,
        v.code,
        v.make,
        v.model,
        v.year,
        v.category,
        v.transmission,
        v.fuel,
        v.fuelType,
        v.location,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !keyword || searchableText.includes(keyword);

      return matchesVehicleStatus && matchesCategory && matchesSearch;
    });
  }, [vehiclesWithBookingAvailability, vehicleStatusFilter, categoryFilter, search, activeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const paginatedVehicles = filteredVehicles.slice(
    pageStartIndex,
    pageStartIndex + ITEMS_PER_PAGE
  );
  const showingStart = filteredVehicles.length === 0 ? 0 : pageStartIndex + 1;
  const showingEnd = Math.min(pageStartIndex + paginatedVehicles.length, filteredVehicles.length);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);
  const selectedOnPageCount = paginatedVehicles.filter((vehicle) =>
    selectedVehicleIds.includes(vehicle._id)
  ).length;
  const isPageSelected =
    paginatedVehicles.length > 0 && selectedOnPageCount === paginatedVehicles.length;

  const vehicleStats = useMemo(() => {
    const available = vehiclesWithBookingAvailability.filter(
      (vehicle) => getOperationalStatus(vehicle) === "available"
    ).length;
    const ongoing = vehiclesWithBookingAvailability.filter(
      (vehicle) => hasOngoingBooking(vehicle)
    ).length;
    const maintenance = vehiclesWithBookingAvailability.filter(
      (vehicle) => getFleetStatus(vehicle) === "maintenance"
    ).length;

    return {
      total: vehiclesWithBookingAvailability.length,
      available,
      ongoing,
      maintenance,
    };
  }, [vehiclesWithBookingAvailability]);
  const activeVehicleStatFilter = getVehicleStatFilterKey(vehicleStatusFilter);
  const vehicleStatCards = [
    {
      key: "all",
      status: "all",
      label: "Total Vehicles",
      value: vehicleStats.total,
      icon: "CAR",
      title: "Show all vehicles",
    },
    {
      key: "available",
      status: "available",
      label: "Available",
      value: vehicleStats.available,
      icon: "OK",
      tone: "success",
      title: "Show available vehicles",
    },
    {
      key: "ongoing",
      status: "ongoing",
      label: "Ongoing",
      value: vehicleStats.ongoing,
      icon: "ON",
      tone: "warning",
      title: "Show ongoing vehicles",
    },
    {
      key: "maintenance",
      status: "maintenance",
      label: "Maintenance",
      value: vehicleStats.maintenance,
      icon: "MT",
      tone: "danger",
      title: "Show maintenance vehicles",
    },
  ];

  const applyVehicleStatusQuickFilter = (status) => {
    setVehicleStatusFilter(status);
  };

  const handleVehicleStatKeyDown = (event, status) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    applyVehicleStatusQuickFilter(status);
  };

  const toggleVehicleSelection = (id) => {
    setSelectedVehicleIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );
  };

  const togglePageSelection = () => {
    const pageIds = paginatedVehicles.map((vehicle) => vehicle._id);

    setSelectedVehicleIds((current) => {
      if (pageIds.every((id) => current.includes(id))) {
        return current.filter((id) => !pageIds.includes(id));
      }

      return [...new Set([...current, ...pageIds])];
    });
  };

  const clearFilters = () => {
    setSearch("");
    setActiveFilter("all");
    setVehicleStatusFilter("all");
    setCategoryFilter("all");
  };

  const getStatusBadgeClass = (value) => {
    if (value === "available") return "badge badge-available";
    if (value === "ongoing") return "badge badge-pending";
    if (value === "maintenance") return "badge badge-rejected";
    if (value === "inactive") return "badge badge-rejected";
    if (value === "not_available") return "badge badge-muted";
    return "badge";
  };

  const getStatusLabel = (value) => {
    if (value === "available") return "Available";
    if (value === "ongoing") return "Ongoing";
    if (value === "maintenance") return "Maintenance";
    if (value === "inactive") return "Inactive";
    if (value === "not_available") return "Not available";
    return value;
  };

  const formatOngoingUntil = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getCapacityLabel = (value) => `${value || "-"} seats`;

  const formatPeso = (value) => {
    return `PHP ${Number(value || 0).toLocaleString()}`;
  };

  const RequiredLabel = ({ label, required = true }) => (
    <label className="vehicle-field-label">
      <span>{label}</span>
      {required ? <b>*</b> : null}
    </label>
  );

  if (!canAccessPage) {
    return <Navigate to="/unauthorized" replace />;
  }

  const vehicleLoadingTitle = isSubmitting
    ? editingId
      ? "Updating vehicle..."
      : "Saving vehicle..."
    : actionLoadingId.startsWith("deactivate:")
    ? "Deactivating vehicle..."
    : actionLoadingId.startsWith("activate:")
    ? "Activating vehicle..."
    : actionLoadingId.startsWith("restore:")
    ? "Restoring vehicle..."
    : "Processing vehicle action...";

  return (
    <AdminShell
      eyebrow="FLEET"
      title="Vehicle Management"
      subtitle="Manage vehicle listings, availability, pricing, photos, and operating status."
      pageClassName="admin-shell--vehicles"
      actions={
        canAddVehicle ? (
          <button type="button" className="btn btn-primary" onClick={openAddModal}>
            Add Vehicle
          </button>
        ) : null
      }
    >
      <AdminLoadingOverlay
        open={isSubmitting || Boolean(actionLoadingId)}
        title={vehicleLoadingTitle}
        message="Please wait while FleetX updates the fleet record."
      />
      {msg ? <div className="dashboard-alert">{msg}</div> : null}

      <div className="dashboard-stats-grid vehicle-stats-grid">
        {vehicleStatCards.map((card) => (
          <div
            key={card.key}
            className={`vehicle-stat-filter-card ${
              activeVehicleStatFilter === card.key ? "is-active" : ""
            }`}
            role="button"
            tabIndex={0}
            title={card.title}
            aria-label={card.title}
            aria-pressed={activeVehicleStatFilter === card.key}
            onClick={() => applyVehicleStatusQuickFilter(card.status)}
            onKeyDown={(event) => handleVehicleStatKeyDown(event, card.status)}
          >
            <StatCard
              label={card.label}
              value={card.value}
              icon={card.icon}
              tone={card.tone}
            />
          </div>
        ))}
      </div>

      <div className="vehicle-toolbar-modern-exact">
        <input
          className="input vehicle-search-exact"
          placeholder="Search vehicles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="select"
          value={vehicleStatusFilter}
          onChange={(e) => setVehicleStatusFilter(e.target.value)}
          disabled={activeFilter === "archived"}
        >
          <option value="all">All</option>
          <option value="available">Available</option>
          <option value="ongoing">Ongoing</option>
          <option value="maintenance">Maintenance</option>
        </select>

        <select
          className="select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All</option>
          {categoryOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>

        <button type="button" className="btn vehicle-clear-btn" onClick={clearFilters}>
          Clear
        </button>
      </div>

      <div className="vehicle-exact-panel">
        <div className="dashboard-table-wrap">
          <table className="vehicle-exact-table">
            <thead>
              <tr>
                <th className="vehicle-select-col">
                  <input
                    type="checkbox"
                    checked={isPageSelected}
                    onChange={togglePageSelection}
                    aria-label="Select all vehicles on this page"
                  />
                </th>
                <th>Plate Number</th>
                <th>Vehicle</th>
                <th>Rate</th>
                <th>Location</th>
                <th>Fleet Status</th>
                <th>Availability Today</th>
                <th>Active Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedVehicles.map((v) => {
                const vehicleImages = getVehicleImages(v);
                const mainImage = vehicleImages[0] || "";
                const isSelected = selectedVehicleIds.includes(v._id);
                const fleetStatus = getFleetStatus(v);
                const availabilityTodayStatus = getAvailabilityTodayStatus(v);
                const ongoingUntil = formatOngoingUntil(
                  v.currentBookingEndDate || v.currentBooking?.endDate
                );

                return (
                <tr key={v._id}>
                  <td className="vehicle-select-col">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleVehicleSelection(v._id)}
                      aria-label={`Select ${v.make} ${v.model}`}
                    />
                  </td>

                  <td>
                    <div className="vehicle-exact-plate">
                      {getVehicleIdentifierText(v) || "No identifier"}
                    </div>
                  </td>

                  <td>
                    <div className="vehicle-exact-info">
                      {mainImage ? (
                        <div className="vehicle-image-hover-wrap">
                          <button
                            type="button"
                            className="vehicle-thumb-btn"
                            onClick={() => {
                              setPreviewImage(getImageSrc(mainImage));
                              setShowImageModal(true);
                            }}
                          >
                            <img
                              src={getImageSrc(mainImage)}
                              alt={getVehicleName(v)}
                              className="vehicle-exact-thumb"
                            />
                          </button>

                          <div className="vehicle-hover-preview">
                            <div className="vehicle-hover-preview-grid">
                              {vehicleImages.map((image, index) => (
                                <img
                                  key={`${image}-${index}`}
                                  src={getImageSrc(image)}
                                  alt={`${getVehicleName(v)} ${index + 1}`}
                                  className="vehicle-hover-preview-img"
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="vehicle-exact-thumb-placeholder">No Image</div>
                      )}

                      <div>
                        <div className="vehicle-exact-name">
                          {getVehicleName(v)}
                        </div>
                        <div className="vehicle-exact-meta">
                          {getVehicleDetailLine(v) || getCapacityLabel(v.seater)}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="vehicle-exact-rate">
                      {formatPeso(v.dailyRate)}
                    </div>
                  </td>

                  <td>
                    <div className="vehicle-exact-location">{v.location}</div>
                  </td>

                  <td>
                    <span className={getStatusBadgeClass(fleetStatus)}>
                      {getStatusLabel(fleetStatus)}
                    </span>
                  </td>

                  <td>
                    <span className={getStatusBadgeClass(availabilityTodayStatus)}>
                      {getStatusLabel(availabilityTodayStatus)}
                    </span>
                    {availabilityTodayStatus === "ongoing" && ongoingUntil ? (
                      <div className="vehicle-exact-meta">
                        Ongoing until {ongoingUntil}
                      </div>
                    ) : null}
                  </td>

                  <td>
                    <span
                      className={
                        v.isArchived
                          ? "badge badge-muted"
                          : v.isActive
                          ? "badge badge-approved"
                          : "badge badge-rejected"
                      }
                    >
                      {v.isArchived ? "Archived" : v.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td>
                    <div className="vehicle-exact-actions">
                      {canEditVehicle && !v.isArchived && (
                        <button
                          type="button"
                          className="vehicle-icon-btn"
                          onClick={() => openEditModal(v)}
                          title="Edit"
                          aria-label="Edit vehicle"
                        >
                          {actionIcons.edit}
                        </button>
                      )}

                      {canManageVehicleStatus && !v.isArchived && (
                        <button
                          type="button"
                          className="vehicle-icon-btn"
                          onClick={() => {
                            if (v.isActive) {
                              deactivate(v._id);
                            } else {
                              activate(v._id);
                            }
                          }}
                          disabled={Boolean(actionLoadingId)}
                          title={v.isActive ? "Deactivate" : "Activate"}
                          aria-label={v.isActive ? "Deactivate vehicle" : "Activate vehicle"}
                        >
                          {v.isActive ? actionIcons.pause : actionIcons.play}
                        </button>
                      )}

                      {canArchiveVehicle && !v.isArchived && (
                        <button
                          type="button"
                          className="vehicle-icon-btn vehicle-icon-btn--danger"
                          onClick={() => openArchiveModal(v)}
                          disabled={Boolean(actionLoadingId)}
                          title="Archive"
                          aria-label="Archive vehicle"
                        >
                          {actionIcons.delete}
                        </button>
                      )}

                      {canArchiveVehicle && v.isArchived && (
                        <button
                          type="button"
                          className="vehicle-icon-btn"
                          onClick={() => restore(v._id)}
                          disabled={Boolean(actionLoadingId)}
                          title="Restore from archive"
                          aria-label="Restore vehicle"
                        >
                          {actionIcons.play}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}

              {filteredVehicles.length === 0 && (
                <tr>
                  <td colSpan="9" className="dashboard-empty-cell">
                    No vehicles found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="vehicle-table-footer">
          <div className="vehicle-bulk-actions">
            <label className="vehicle-select-all">
              <input
                type="checkbox"
                checked={isPageSelected}
                onChange={togglePageSelection}
                disabled={paginatedVehicles.length === 0}
              />
              <span>Select all</span>
            </label>
            <select className="select vehicle-more-actions" disabled={selectedVehicleIds.length === 0}>
              <option>More actions</option>
              <option>Activate selected</option>
              <option>Deactivate selected</option>
            </select>
          </div>

          <div className="vehicle-pagination-wrap">
            <span className="vehicle-pagination-summary">
              Showing {showingStart} to {showingEnd} of {filteredVehicles.length} vehicles
            </span>
            <nav className="vehicle-pagination" aria-label="Vehicle pagination">
              <button
                type="button"
                className="vehicle-page-btn"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={safeCurrentPage === 1}
                aria-label="Previous page"
              >
                &lt;
              </button>
              {pageNumbers.map((page) => (
                <button
                  type="button"
                  key={page}
                  className={`vehicle-page-btn ${page === safeCurrentPage ? "is-active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                className="vehicle-page-btn"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={safeCurrentPage === totalPages}
                aria-label="Next page"
              >
                &gt;
              </button>
            </nav>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay vehicle-modal-overlay" onClick={closeModal}>
          <div
            className="modal-box vehicle-modal-box vehicle-modal-box--composer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vehicle-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={submitVehicle} className="vehicle-modal-shell">
              <div className="vehicle-modal-top">
                <div className="vehicle-modal-titleBlock">
                  <span className="vehicle-modal-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M5.2 7.5 6.4 4h11.2l1.2 3.5 1.7.5c.9.3 1.5 1.1 1.5 2.1V17h-2v2h-3v-2H7v2H4v-2H2v-6.9c0-1 .6-1.8 1.5-2.1l1.7-.5ZM7.8 6 7 8h10l-.8-2H7.8ZM5.5 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm13 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" /></svg>
                  </span>
                  <div>
                    <h3 id="vehicle-modal-title">{editingId ? "Edit Vehicle" : "Add New Vehicle"}</h3>
                    <p>
                      Create a new vehicle listing for your fleet. Fill in the details below,
                      upload high-quality photos, and publish.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="vehicle-modal-close"
                  onClick={closeModal}
                  aria-label="Close vehicle form"
                >
                  X
                </button>
              </div>

              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                multiple
                className="vehicle-file-input"
                onChange={(e) => {
                  addImageFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                ref={replaceInputRef}
                type="file"
                accept="image/*"
                className="vehicle-file-input"
                onChange={(e) => {
                  replaceImageFile(e.target.files);
                  e.target.value = "";
                }}
              />

              <div className="vehicle-modal-scroll">
                {modalNotice ? (
                  <div className="vehicle-modal-note">{modalNotice}</div>
                ) : null}

                <div className="vehicle-modal-layout">
                  <div className="vehicle-composer-main">
                    <section className="vehicle-form-section">
                      <div className="vehicle-form-section-head">
                        <span className="vehicle-section-number">1</span>
                        <div><h4>Basic Information</h4><p>Enter the vehicle's core identifying details.</p></div>
                      </div>

                      <div className="vehicle-form-grid vehicle-form-grid--basic">
                        <div className="field-group">
                          <RequiredLabel label="Plate Number" />
                          <input
                            className={`input ${errors.plateNo ? "input-error" : ""}`}
                            value={formData.plateNo}
                            placeholder="e.g. ABC 1234"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                plateNo: e.target.value.toUpperCase(),
                              })
                            }
                          />
                          {errors.plateNo ? (
                            <span className="field-error">{errors.plateNo}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Make" />
                          <input
                            className={`input ${errors.make ? "input-error" : ""}`}
                            value={formData.make}
                            placeholder="e.g. Toyota"
                            onChange={(e) =>
                              setFormData({ ...formData, make: e.target.value })
                            }
                          />
                          {errors.make ? (
                            <span className="field-error">{errors.make}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Model" />
                          <input
                            className={`input ${errors.model ? "input-error" : ""}`}
                            value={formData.model}
                            placeholder="e.g. Vios"
                            onChange={(e) =>
                              setFormData({ ...formData, model: e.target.value })
                            }
                          />
                          {errors.model ? (
                            <span className="field-error">{errors.model}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Year" />
                          <input
                            className={`input ${errors.year ? "input-error" : ""}`}
                            value={formData.year}
                            placeholder="e.g. 2022"
                            maxLength={4}
                            inputMode="numeric"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                year: e.target.value.replace(/\D/g, ""),
                              })
                            }
                          />
                          {errors.year ? (
                            <span className="field-error">{errors.year}</span>
                          ) : null}
                        </div>

                        <div className="field-group vehicle-color-field">
                          <RequiredLabel label="Color" />
                          <input
                            className={`input ${errors.color ? "input-error" : ""}`}
                            value={formData.color}
                            maxLength={40}
                            placeholder="e.g. Pearl White"
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          />
                          {errors.color ? <span className="field-error">{errors.color}</span> : null}
                        </div>
                      </div>
                    </section>

                    <section className="vehicle-form-section">
                      <div className="vehicle-form-section-head">
                        <span className="vehicle-section-number">2</span>
                        <div><h4>Vehicle Specifications</h4><p>Add capacity, drivetrain, and comfort details.</p></div>
                      </div>

                      <div className="vehicle-form-grid vehicle-form-grid--specs">
                        <div className="field-group">
                          <RequiredLabel label="Category" />
                          <select
                            className={`select ${errors.category ? "input-error" : ""}`}
                            value={formData.category}
                            onChange={(e) =>
                              setFormData({ ...formData, category: e.target.value })
                            }
                          >
                            {categoryOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.category ? (
                            <span className="field-error">{errors.category}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Seater" />
                          <input
                            className={`input ${errors.seater ? "input-error" : ""}`}
                            value={formData.seater}
                            inputMode="numeric"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                seater: e.target.value.replace(/\D/g, ""),
                              })
                            }
                          />
                          {errors.seater ? (
                            <span className="field-error">{errors.seater}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Transmission" />
                          <select
                            className={`select ${errors.transmission ? "input-error" : ""}`}
                            value={formData.transmission}
                            onChange={(e) =>
                              setFormData({ ...formData, transmission: e.target.value })
                            }
                          >
                            {transmissionOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.transmission ? (
                            <span className="field-error">{errors.transmission}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Fuel" />
                          <select
                            className={`select ${errors.fuel ? "input-error" : ""}`}
                            value={formData.fuel}
                            onChange={(e) =>
                              setFormData({ ...formData, fuel: e.target.value })
                            }
                          >
                            {fuelOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.fuel ? (
                            <span className="field-error">{errors.fuel}</span>
                          ) : null}
                        </div>

                        <div className="field-group vehicle-coding-field">
                          <RequiredLabel label="Coding" />
                          <select
                            className={`select ${errors.coding ? "input-error" : ""}`}
                            value={formData.coding}
                            onChange={(e) =>
                              setFormData({ ...formData, coding: e.target.value })
                            }
                          >
                            {codingOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          {errors.coding ? (
                            <span className="field-error">{errors.coding}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Luggage Bags" required={false} />
                          <input
                            className={`input ${errors.luggageBags ? "input-error" : ""}`}
                            value={formData.luggageBags}
                            inputMode="numeric"
                            placeholder="e.g. 3"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                luggageBags: e.target.value.replace(/\D/g, ""),
                              })
                            }
                          />
                          {errors.luggageBags ? (
                            <span className="field-error">{errors.luggageBags}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Doors" required={false} />
                          <input
                            className={`input ${errors.doors ? "input-error" : ""}`}
                            value={formData.doors}
                            inputMode="numeric"
                            placeholder="e.g. 4"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                doors: e.target.value.replace(/\D/g, ""),
                              })
                            }
                          />
                          {errors.doors ? (
                            <span className="field-error">{errors.doors}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Air Conditioning" required={false} />
                          <input
                            className={`input ${errors.aircon ? "input-error" : ""}`}
                            value={formData.aircon}
                            placeholder="e.g. Yes / Dual Aircon"
                            onChange={(e) =>
                              setFormData({ ...formData, aircon: e.target.value })
                            }
                          />
                          {errors.aircon ? (
                            <span className="field-error">{errors.aircon}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Engine" required={false} />
                          <input
                            className={`input ${errors.engine ? "input-error" : ""}`}
                            value={formData.engine}
                            placeholder="e.g. 2.8L Diesel Turbo"
                            onChange={(e) =>
                              setFormData({ ...formData, engine: e.target.value })
                            }
                          />
                          {errors.engine ? (
                            <span className="field-error">{errors.engine}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Fuel Economy" required={false} />
                          <input
                            className={`input ${errors.fuelEconomy ? "input-error" : ""}`}
                            value={formData.fuelEconomy}
                            placeholder="e.g. 12 km/L"
                            onChange={(e) =>
                              setFormData({ ...formData, fuelEconomy: e.target.value })
                            }
                          />
                          {errors.fuelEconomy ? (
                            <span className="field-error">{errors.fuelEconomy}</span>
                          ) : null}
                        </div>
                      </div>
                    </section>

                    <section className="vehicle-form-section">
                      <div className="vehicle-form-section-head">
                        <span className="vehicle-section-number">3</span>
                        <div><h4>Pricing &amp; Availability</h4><p>Set rental rates and the current fleet status.</p></div>
                      </div>

                      <div className="vehicle-form-grid vehicle-form-grid--pricing">
                        <div className="field-group">
                          <RequiredLabel label="Daily Rate" />
                          <input
                            className={`input ${errors.rate24Hr ? "input-error" : ""}`}
                            value={formData.rate24Hr}
                            inputMode="decimal"
                            placeholder="e.g. 2500"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                rate24Hr: e.target.value.replace(/[^\d.]/g, ""),
                                dailyRate: e.target.value.replace(/[^\d.]/g, ""),
                              })
                            }
                          />
                          <small>Daily rate is charged per 24-hour rental day.</small>
                          {errors.rate24Hr ? (
                            <span className="field-error">{errors.rate24Hr}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Extra-hour Rate (₱)" />
                          <input
                            className={`input ${errors.extraHourRate ? "input-error" : ""}`}
                            value={formData.extraHourRate}
                            inputMode="decimal"
                            placeholder="e.g. 250"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                extraHourRate: e.target.value.replace(/[^\d.]/g, ""),
                              })
                            }
                          />
                          <small>Used for approved extensions or late returns beyond the booked return time.</small>
                          {errors.extraHourRate ? (
                            <span className="field-error">{errors.extraHourRate}</span>
                          ) : null}
                        </div>

                        <div className="field-group">
                          <RequiredLabel label="Status" />
                          <select
                            className={`select ${errors.status ? "input-error" : ""}`}
                            value={formData.status}
                            onChange={(e) =>
                              setFormData({ ...formData, status: e.target.value })
                            }
                          >
                            <option value="available">Available</option>
                            <option value="maintenance">Maintenance</option>
                          </select>
                          {errors.status ? (
                            <span className="field-error">{errors.status}</span>
                          ) : null}
                        </div>
                      </div>
                    </section>

                    <section className="vehicle-form-section">
                      <div className="vehicle-form-section-head">
                        <span className="vehicle-section-number">4</span>
                        <div><h4>Location</h4><p>Set the default pickup location for this vehicle.</p></div>
                      </div>

                      <div className="field-group">
                        <RequiredLabel label="Location / Pickup Location" />
                        <GooglePlacesAutocomplete
                          apiMode="new"
                          inputClassName={`input ${errors.location ? 'input-error' : ''}`}
                          className={`input ${errors.location ? "input-error" : ""}`}
                          value={formData.location}
                          placeholder="e.g. NAIA Terminal 3, Pasay City"
                          onChange={(value) => {
                            setFormData((current) => ({
                              ...current,
                              location: value,
                              locationPlaceId: String(),
                              locationLatitude: null,
                              locationLongitude: null,
                              locationRequiresSelection: true,
                            }));
                            setErrors((current) => ({ ...current, location: undefined }));
                          }}
                          onPlaceSelect={({ address, coords, placeId }) => {
                            setFormData((current) => ({
                              ...current,
                              location: address,
                              locationPlaceId: placeId || String(),
                              locationLatitude: coords?.lat ?? null,
                              locationLongitude: coords?.lng ?? null,
                              locationRequiresSelection: !placeId || !coords,
                            }));
                            setErrors((current) => ({ ...current, location: undefined }));
                          }}
                        />
                        {errors.location ? (
                          <span className="field-error">{errors.location}</span>
                        ) : null}
                      </div>
                    </section>

                    <section className="vehicle-form-section vehicle-form-section--description">
                      <div className="vehicle-form-section-head">
                        <span className="vehicle-section-number">5</span>
                        <div><h4>Description &amp; Features</h4><p>Summarize the vehicle and its key amenities.</p></div>
                        <span className="vehicle-char-count">
                          {formData.description.length}/{DESCRIPTION_MAX_LENGTH}
                        </span>
                      </div>

                      <div className="field-group">
                        <RequiredLabel label="Description" />
                        <textarea
                          className={`input vehicle-description-input ${
                            errors.description ? "input-error" : ""
                          }`}
                          value={formData.description}
                          maxLength={DESCRIPTION_MAX_LENGTH}
                          rows={4}
                          placeholder="Describe the vehicle, its condition, ideal use, and any important details..."
                          onChange={(e) =>
                            setFormData({ ...formData, description: e.target.value })
                          }
                        />
                        {errors.description ? (
                          <span className="field-error">{errors.description}</span>
                        ) : null}
                      </div>

                      <div className="field-group">
                        <RequiredLabel label="Amenities / Features" required={false} />
                        <textarea
                          className={`input vehicle-description-input ${
                            errors.features ? "input-error" : ""
                          }`}
                          value={formData.features}
                          maxLength={FEATURES_MAX_LENGTH}
                          rows={4}
                          placeholder="e.g. Reverse Camera, Apple CarPlay, Cruise Control"
                          onChange={(e) =>
                            setFormData({ ...formData, features: e.target.value })
                          }
                        />
                        {errors.features ? (
                          <span className="field-error">{errors.features}</span>
                        ) : null}
                      </div>
                    </section>
                  </div>

                  <aside className="vehicle-composer-side">
                    <section className="vehicle-side-card vehicle-photo-manager">
                      <div className="vehicle-side-head">
                        <div>
                          <h4>Vehicle Photos</h4>
                          <p>
                            Upload up to 3 clear vehicle photos. The first photo will be
                            used as the main thumbnail.
                          </p>
                        </div>
                        <span>{imageItems.length}/{MAX_VEHICLE_IMAGES}</span>
                      </div>

                      <div
                        className={`vehicle-photo-dropzone ${
                          isDraggingImages ? "is-dragging" : ""
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(true);
                        }}
                        onDragLeave={() => setIsDraggingImages(false)}
                        onDrop={handleImageDrop}
                      >
                        <button
                          type="button"
                          className="vehicle-photo-upload-box"
                          onClick={() => uploadInputRef.current?.click()}
                        >
                          <span className="vehicle-photo-empty-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24"><path d="M11 16V7.8L8.4 10.4 7 9l5-5 5 5-1.4 1.4L13 7.8V16h-2Zm-5 4a2 2 0 0 1-2-2v-3h2v3h12v-3h2v3a2 2 0 0 1-2 2H6Z" /></svg>
                          </span>
                          <strong>Drag & drop photos here</strong>
                          <em>or click to browse</em>
                          <small>PNG, JPG or WEBP up to 5MB each</small>
                        </button>

                        <div className="vehicle-photo-slot-grid">
                          {Array.from({ length: MAX_VEHICLE_IMAGES }).map((_, imageIndex) => {
                            const item = imageItems[imageIndex];

                            return item ? (
                              <div className="vehicle-photo-slot" key={item.id}>
                                <img
                                  src={item.previewUrl}
                                  alt={`Vehicle ${imageIndex + 1}`}
                                  className="vehicle-photo-slot-img"
                                />
                                {imageIndex === 0 ? (
                                  <span className="vehicle-main-badge">Main Photo</span>
                                ) : null}
                                <div className="vehicle-photo-actions">
                                  <button
                                    type="button"
                                    onClick={() => openReplacePicker(imageIndex)}
                                  >
                                    Replace
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeImage(imageIndex)}
                                  >
                                    Remove
                                  </button>
                                  <button
                                    type="button"
                                    disabled={imageIndex === 0}
                                    onClick={() => moveImage(imageIndex, -1)}
                                  >
                                    Up
                                  </button>
                                  <button
                                    type="button"
                                    disabled={imageIndex === imageItems.length - 1}
                                    onClick={() => moveImage(imageIndex, 1)}
                                  >
                                    Down
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="vehicle-photo-slot vehicle-photo-slot--empty"
                                key={`empty-${imageIndex}`}
                                onClick={() => uploadInputRef.current?.click()}
                              >
                                <span>+</span>
                                <small>IMG</small>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {errors.images ? (
                        <span className="field-error">{errors.images}</span>
                      ) : null}
                    </section>

                    <section className="vehicle-side-card vehicle-guidelines-card">
                      <div className="vehicle-side-head">
                        <div>
                          <h4>Photo Guidelines</h4>
                        </div>
                      </div>
                      <div className="vehicle-guidance-list">
                        <span>Upload front, side, and interior/back photo</span>
                        <span>Use clear daylight images</span>
                        <span>Avoid blurry or cropped photos</span>
                        <span>First image becomes the main thumbnail</span>
                      </div>
                    </section>

                    <section className="vehicle-side-card vehicle-checklist-card">
                      <div className="vehicle-side-head">
                        <div>
                          <h4>Publishing Checklist</h4>
                        </div>
                      </div>
                      <div className="vehicle-checklist-list">
                        <span className={formData.make && formData.model && formData.year && formData.color ? "is-complete" : ""}>
                          Vehicle details completed
                        </span>
                        <span className={formData.dailyRate && formData.location ? "is-complete" : ""}>
                          Rate and location added
                        </span>
                        <span className={imageItems.length > 0 ? "is-complete" : ""}>
                          At least 1 photo uploaded
                        </span>
                        <span className={formData.status === "available" ? "is-complete" : ""}>
                          Status set to Available
                        </span>
                      </div>
                    </section>
                  </aside>
                </div>
              </div>

              <div className="vehicle-modal-footer">
                <button
                  type="button"
                  className="btn"
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-soft"
                  onClick={saveDraftNotice}
                  disabled={isSubmitting}
                >
                  Save Draft
                </button>
                <button
                  type="submit"
                  className="btn btn-primary vehicle-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="btn-spinner" />
                  ) : editingId ? (
                    "Save Changes"
                  ) : (
                    "Publish Vehicle"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImageModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowImageModal(false);
            setPreviewImage("");
          }}
        >
          <div className="image-preview-box" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn-close image-preview-close"
              onClick={() => {
                setShowImageModal(false);
                setPreviewImage("");
              }}
            >
              Close
            </button>
            <img
              src={previewImage}
              alt="Vehicle Preview"
              className="image-preview-large"
            />
          </div>
        </div>
      )}

      {confirmAction ? (
        <div className="modal-overlay" onClick={() => !actionLoadingId && setConfirmAction(null)}>
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vehicle-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="vehicle-confirm-title" className="admin-section-title" style={{ margin: 0 }}>
                  {confirmAction.title}
                </h3>
                <span className="muted">Please confirm before this action is applied.</span>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setConfirmAction(null)}
                disabled={Boolean(actionLoadingId)}
              >
                Close
              </button>
            </div>
            <p className="muted">{confirmAction.message}</p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setConfirmAction(null)}
                disabled={Boolean(actionLoadingId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${confirmAction.tone === "danger" ? "btn-danger" : "btn-primary"}`}
                onClick={confirmAction.onConfirm}
                disabled={Boolean(actionLoadingId)}
              >
                {actionLoadingId ? confirmAction.loadingLabel || "Processing..." : confirmAction.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {archiveModal ? (
        <div
          className="modal-overlay"
          onClick={() => !archiveModal.submitting && setArchiveModal(null)}
        >
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vehicle-archive-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="vehicle-archive-title" className="admin-section-title" style={{ margin: 0 }}>
                  Archive vehicle?
                </h3>
                <span className="muted">
                  {archiveModal.vehicle
                    ? `${archiveModal.vehicle.make} ${archiveModal.vehicle.model} (${archiveModal.vehicle.plateNo})`
                    : ""}
                </span>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setArchiveModal(null)}
                disabled={archiveModal.submitting}
              >
                Close
              </button>
            </div>
            <p className="muted" style={{ marginBottom: 8 }}>
              Archived vehicles are removed from customer searches and booking. They can be restored later.
            </p>
            <div className="field-group" style={{ marginBottom: 8 }}>
              <label className="vehicle-field-label">
                <span>Reason for archiving</span>
                <b>*</b>
              </label>
              <select
                className="select"
                value={
                  [
                    "End of service life",
                    "Long-term maintenance",
                    "Sold or transferred",
                    "Regulatory hold",
                    "Other",
                  ].includes(archiveModal.reason)
                    ? archiveModal.reason
                    : archiveModal.reason ? "Other" : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val !== "Other" && val !== "") {
                    setArchiveModal((prev) => ({ ...prev, reason: val, error: "" }));
                  } else if (val === "Other") {
                    setArchiveModal((prev) => ({ ...prev, reason: "", error: "" }));
                  }
                }}
                disabled={archiveModal.submitting}
              >
                <option value="">Select a reason...</option>
                <option>End of service life</option>
                <option>Long-term maintenance</option>
                <option>Sold or transferred</option>
                <option>Regulatory hold</option>
                <option>Other</option>
              </select>
            </div>
            <div className="field-group" style={{ marginBottom: 4 }}>
              <textarea
                className="input"
                rows={3}
                placeholder="Describe the reason for archiving this vehicle..."
                value={archiveModal.reason}
                disabled={archiveModal.submitting}
                onChange={(e) =>
                  setArchiveModal((prev) => ({ ...prev, reason: e.target.value, error: "" }))
                }
              />
            </div>
            {archiveModal.error ? (
              <p className="field-error" style={{ marginBottom: 8 }}>
                {archiveModal.error}
              </p>
            ) : null}
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setArchiveModal(null)}
                disabled={archiveModal.submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmArchive}
                disabled={archiveModal.submitting}
              >
                {archiveModal.submitting ? "Archiving..." : "Archive Vehicle"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
